<script setup>
defineOptions({
    name: 'ContractClientSelectionModal',
});

/*
|--------------------------------------------------------------------------
| ContractClientSelectionModal.vue
|--------------------------------------------------------------------------
| Purpose
|   Modal for pre-selecting a client before creating a new contract.
|   Used by the dashboard tile (+), sidebar "Nieuw contract", and the
|   contracts index "Toevoegen" button for sr-coordinator / contract-manager.
|
| Usage (parent):
|   <ContractClientSelectionModal v-model="showModal" @select="onSelect" />
|
| Props
|   - modelValue (Boolean): controls modal visibility via v-model
|
| Events
|   - update:modelValue(Boolean): emitted when the modal opens/closes
|   - select(Object): emitted when the user chooses a client
|   - close: emitted when modal is closed
*/

import { ref, computed, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import Modal from '@/components/Modal.vue';
import FormRichSelect from '@/components/Form/FormRichSelect.vue';

const instance = getCurrentInstance();
const route = instance?.appContext.config.globalProperties.route;

const props = defineProps({
    /** Controls modal visibility - accepts boolean or truthy value (e.g. tile key) */
    modelValue: { type: [Boolean, String], default: false },
});

const emit = defineEmits(['update:modelValue', 'select', 'close']);

const show = computed({
    get: () => !!props.modelValue,
    set: (val) => emit('update:modelValue', val),
});

const selectedClient = ref(null);

const searchUrl = computed(() => route('data.clients') + '?selectable_for_new_contracts=1');

function handleSelect() {
    if (!selectedClient.value) return;

    const clientId = selectedClient.value?.value ?? selectedClient.value;
    if (!clientId) return;

    emit('select', { client_id: clientId });
    router.visit(route('contracts.create', { client_id: clientId }));
    close();
}

function close() {
    show.value = false;
    selectedClient.value = null;
    emit('close');
}
</script>

<template>
    <Modal
        :show="show"
        title="Nieuw contract"
        size="lg"
        theme="orange"
        confirm-button-variant="orange"
        :confirm-button-disabled="!selectedClient"
        :show-default-footer="true"
        @close="close"
        @cancel="close"
        @confirm="handleSelect"
        :cancel-button-id="'input-contract-client-modal-cancel'"
        :confirm-button-id="'input-contract-client-modal-select'"
    >
        <div class="contract-client-selection-modal-content">
            <p class="modal-description">
                Selecteer eerst een opdrachtgever voordat u een nieuw contract start.
            </p>

            <div class="contract-client-selection-field">
                <label for="input-contract-client-selection" class="form-label">
                    Opdrachtgever
                </label>
                <FormRichSelect
                    id="input-contract-client-selection"
                    v-model="selectedClient"
                    :search-url="searchUrl"
                    placeholder="Zoek een opdrachtgever..."
                    option-label="label"
                    option-value="value"
                    class="wide-dropdown"
                />
            </div>
        </div>
    </Modal>
</template>
